import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppDB } from 'src/app/config/idb';
import { IBodyUploadFile } from 'src/app/interfaces/Inputs';
import { IPhotoAnalysisClientRequest } from 'src/app/interfaces/Inputs/PhotoAnalysis';
import { IPhotoAnalysisNotification } from 'src/app/interfaces/Outputs/PhotoAnalysis';
import { EExtension, Extension, INotUploadedFileData, Media, PhotoStep, TanalysisStatus } from 'src/app/interfaces/PWA/Files';
import { EInspectionStep } from 'src/app/interfaces/PWA/Inspection';
import { FileService } from 'src/app/services/General/File/file.service';
import { EmptyFileError, FileSizeLimitError, PhotoRejectedError } from 'src/app/errors/custom';
import { locateAnalyzedFile } from 'src/app/utils/PhotoAnalysisMatch';
import { planReconciliation } from 'src/app/utils/StateReconciliation';
import {
  ANALYSIS_FAILED_MESSAGE,
  findStalePending,
  msUntilNextDegradation,
} from 'src/app/utils/PendingDegradation';
import {
  findMissingRequiredPhotos,
  requiredStorageFileNames,
  MissingRequiredPhoto,
  RequiredPhotoRef,
} from 'src/app/utils/RequiredPhotos';
import { StorageCheckOutcome, storageFileNameOf } from 'src/app/utils/StorageCheck';
import { UploadTelemetryService } from 'src/app/services/General/Telemetry/upload-telemetry.service';
import { CaptureMethod } from 'src/app/utils/UploadTelemetry';
import { InspectionApiService } from 'src/app/services/InspectionHandlers/InspectionApi/inspection-api.service';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { AIDataService } from '../../InspectionHandlers/aiData.service';
import { HAS_REACHED_FINAL_STEP } from 'src/app/interfaces/Global/Session';

@Injectable({
  providedIn: 'root',
})
export class PhotoStorageService {

  photoResultsAdded$ = new EventEmitter<{ photoId: number }>();

  /**
   * Avance de la subida en curso: 0-100 mientras sube, `null` cuando no hay ninguna.
   *
   * El porcentaje ya se calculaba en `api.service.ts` y `uploadPhoto` lo reenviaba, pero ningún
   * llamador lo pasaba, así que se descartaba y la interfaz sólo tenía un booleano. Vive en el
   * servicio y no en un parámetro porque el servicio es singleton: así cualquier componente lo
   * consume sin que las firmas públicas cambien.
   *
   * `null` y no `0` cuando termina: cero es un avance válido —el primer evento de una subida que
   * arranca— y confundirlos deja la barra en cero en vez de esconderla.
   */
  readonly uploadProgress$ = new BehaviorSubject<number | null>(null);

  /** Reenvía el avance al sujeto y lo apaga al terminar, pase lo que pase. */
  private async conProgreso<T>(accion: (onProgress: (p: number) => void) => Promise<T>): Promise<T> {
    try {
      return await accion((percent) => this.uploadProgress$.next(percent));
    } finally {
      this.uploadProgress$.next(null);
    }
  }

  /** Un solo temporizador, al vencimiento exacto del `pending` mas viejo. No hay sondeo. */
  private degradationTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly DB: AppDB,
    private readonly inspectionApiService: InspectionApiService,
    private readonly fileService: FileService,
    private readonly aiDataService: AIDataService,
    private readonly telemetry: UploadTelemetryService,
  ) { }

  /**
   * Si el archivo es una imagen, la comprime en cliente (2048px / JPEG q80)
   * antes de subir. El resultado comprimido siempre es JPEG, por lo que la
   * extensión declarada pasa a 'jpg'. Para no-imágenes (pdf, mp4) retorna el
   * archivo original sin cambios.
   *
   * Es el único punto por el que pasan los cuatro caminos de subida antes de pedir la URL
   * prefirmada, así que acá viven todas las validaciones previas. Todas lanzan `PhotoRejectedError`,
   * que el llamador traduce en alerta al usuario y la cola trata como fallo permanente:
   *   - archivo de 0 bytes, para cualquier tipo
   *   - peso sobre el límite, medido sobre el resultado FINAL (post-compresión)
   *   - imagen ilegible o completamente negra, medidas sobre el blob que se sube
   */
  private async prepareImageForUpload(
    file: Media,
    ext: Extension
  ): Promise<{ file: Media; ext: Extension; darkPixelRatio?: number }> {
    // Vale para todo tipo, no solo imágenes: en WebKit un File es un handle al archivo en disco y
    // puede quedarse sin respaldo con los metadatos ya cacheados, así que el PUT sube 0 bytes.
    if (file.size === 0) {
      throw new EmptyFileError(
        'El archivo está vacío (0 bytes). Vuelve a capturarlo o selecciónalo de nuevo.'
      );
    }
    const isImage =
      ext === EExtension.jpg || ext === EExtension.jpeg || ext === EExtension.png;
    if (!isImage) {
      return { file, ext };
    }
    const compressed = await this.fileService.compressImage(file);
    let result: { file: Media; ext: Extension };
    if (compressed === file) {
      result = { file, ext };
    } else {
      console.log(
        `[PHOTO-UPLOAD] Imagen comprimida: ${Math.round(file.size / 1024)} KB -> ${Math.round(compressed.size / 1024)} KB`
      );
      result = { file: compressed, ext: EExtension.jpg };
    }
    const limitMB = FileService.MAX_FILE_SIZE_MB[result.ext];
    if (limitMB && result.file.size > limitMB * 1024 * 1024) {
      const sizeMB = (result.file.size / (1024 * 1024)).toFixed(1);
      throw new FileSizeLimitError(
        `La foto pesa ${sizeMB} MB incluso después de comprimirla y supera el máximo permitido de ${limitMB} MB. Selecciona una imagen más liviana.`
      );
    }
    // Sobre `result.file`, que es el blob que se sube: es el mismo objeto que el servidor va a
    // analizar, y medir cualquier otra cosa rompe la paridad.
    const darkPixelRatio = await this.fileService.assertImageContentIsUploadable(result.file);
    return { ...result, darkPixelRatio };
  }

  public async addFormPhoto(
    elementId: number,
    file: Media,
    ext: Extension,
    options?: { captureMethod?: CaptureMethod },
  ) {
    const startTime = Date.now();
    const attemptId = uuidv4();

    // Este camino corre las MISMAS validaciones que `addPhotoAndDeclare` —las dos llaman a
    // `prepareImageForUpload`— así que sus intentos cuentan igual para la cobertura del 100 %.
    //
    // Emite en las CUATRO fases, y hasta acá emitía solo el rechazo de preparación. Lo que cambió
    // no es una preferencia de simetría: una foto de formulario **no se declara**
    // —`getFileDeclareKeysAndPresignedUrl` va sin `photo_id` y no hay `declarePhoto` detrás— así
    // que no tiene uuid de registro, y el esquema del servidor lo exigía en todo lo que no fuera
    // rechazo de cliente. Con eso, su éxito y su fallo se descartaban del lado del servidor: 200
    // con el evento contado en `rejected`, invisible desde el teléfono. El campo `origin` es lo
    // que ahora le permite al servidor exigir `uuid` solo donde hay declaración que cruzar.
    //
    // El `key` sí existe y sí se manda en cuanto se pidió la prefirmada: es la llave con la que la
    // Lambda busca el objeto (`PA_APP_WEB_VERIFICA_ARCHIVO`), y es lo que vuelve cruzable un éxito
    // de formulario sin necesidad de fabricar un uuid que no referenciaría nada.
    let uploadedSizeBytes: number | undefined;
    let darkPixelRatio: number | undefined;
    let keyPath: string | undefined;
    let prepareMs: number | undefined;
    let uploadMs: number | undefined;

    const emitir = (
      outcome: 'success' | 'client_rejection' | 'upload_failure',
      rejectionCause?: string,
    ) =>
      this.telemetry.emit({
        // `photoId` no viaja al servidor —el evento de cable no lo declara— pero el tipo lo exige.
        // Si alguna vez se cablea, ACÁ hay que dejar de pasar un id de elemento de formulario: es
        // otra numeración y se mezclaría con la de las fotos de inspección.
        attemptId, photoId: elementId, ext,
        captureMethod: options?.captureMethod,
        origin: 'formulario',
        outcome,
        rejectionCause,
        uploadedSizeBytes, darkPixelRatio, keyPath, prepareMs, uploadMs,
        totalMs: Date.now() - startTime,
      });

    try {
      const { file: uploadFile, ext: uploadExt, darkPixelRatio: ratio } =
        await this.prepareImageForUpload(file, ext);
      darkPixelRatio = ratio;
      uploadedSizeBytes = uploadFile.size;
      prepareMs = Date.now() - startTime;

      const fileKeys =
        await this.inspectionApiService.getFileDeclareKeysAndPresignedUrl(uploadExt);
      keyPath = fileKeys.keyPath;

      const uploadStart = Date.now();
      await this.conProgreso((onProgress) =>
        this.inspectionApiService.uploadPhoto(fileKeys, uploadFile, onProgress)
      );
      uploadMs = Date.now() - uploadStart;

      // La escritura local va DENTRO del try: si falla, el objeto ya está en S3 y el intento no fue
      // un éxito para el usuario —la foto no queda asociada al elemento del formulario—. Emitirlo
      // como éxito por haber subido dejaría ese caso sin ninguna señal.
      await this.DB.addFormElementPhoto(elementId, fileKeys.publicUrl);
      emitir('success');
      return fileKeys;
    } catch (error: any) {
      emitir(
        error instanceof PhotoRejectedError ? 'client_rejection' : 'upload_failure',
        error instanceof PhotoRejectedError ? error.name : undefined,
      );
      throw error;
    }
  }

  public async addPhotoAndDeclare(
    photoId: number,
    photoName: string,
    photoTag: string,
    file: Media,
    ext: Extension,
    options?: {
      shouldSendToAI?: boolean;
      captureMethod?: CaptureMethod;
      declaredSizeBytes?: number;
      realSizeBytes?: number;
    }
  ) {
    const startTime = Date.now();
    // Identifica el intento aunque no llegue a haber objeto en S3: un rechazo de cliente no tiene
    // key ni uuid —el archivo nunca se subió—, así que esto es lo único que lo nombra.
    const attemptId = uuidv4();
    let darkPixelRatio: number | undefined;
    let uploadedSizeBytes: number | undefined;
    let fileUuid: string | undefined;
    let keyPath: string | undefined;
    let prepareMs: number | undefined;
    let uploadMs: number | undefined;
    console.log(`[PHOTO-UPLOAD] Start: photoId=${photoId}, ext=${ext}`);

    // El try EXTERIOR es solo de telemetría: emite y re-lanza tal cual. El que encola es el
    // INTERIOR; que un rechazo no entre a la cola lo sostienen las dos guardas que se explican abajo.
    try {
      // Compresión en cliente (máx 2048px, JPEG q80) + las validaciones de contenido y peso.
      //
      // Va fuera del try interior, y no es la única guarda: el catch interior también re-lanza un
      // `PhotoRejectedError` antes de encolar, porque el rechazo de policy llega durante la subida.
      // Para un rechazo de validación las dos se tapan: meterla adentro con el catch intacto no
      // rompe nada. Sin ninguna de las dos, un rechazo rompe TRES cosas:
      //   1. se encola para reintento, y una foto negra o de 0 bytes nunca va a dejar de serlo — la
      //      cola no drena;
      //   2. el catch re-lanza un `Error` genérico y se pierde el tipo, así que photo.page deja de
      //      reconocerlo como rechazo de negocio y el usuario recibe un reporte a Sentry en vez de
      //      la alerta con el motivo, y la telemetría lo cuenta como `upload_failure`;
      //   3. sin el tipo, el `continue` de uploadFailedPhotos tampoco aplica y el rechazo pasa a
      //      contar como fallo consecutivo, capaz de cortar el loop de la cola.
      // Las dos primeras las fija `npm run verify:retry-policy`; la tercera, `npm run verify:queue-drain`.
      const { file: uploadFile, ext: uploadExt, darkPixelRatio: ratio } =
        await this.prepareImageForUpload(file, ext);
      darkPixelRatio = ratio;
      uploadedSizeBytes = uploadFile.size;
      prepareMs = Date.now() - startTime;

      try {
        console.log(`[PHOTO-UPLOAD] Getting signed URL...`);
        const fileKeys = await this.inspectionApiService.getFileDeclareKeysAndPresignedUrl(uploadExt, photoId);
        keyPath = fileKeys.keyPath;
        console.log(`[PHOTO-UPLOAD] Got signed URL`);
        console.log(`[PHOTO-UPLOAD] Uploading photo to S3...`);
        const uploadStart = Date.now();
        await this.conProgreso((onProgress) =>
          this.inspectionApiService.uploadPhoto(fileKeys, uploadFile, onProgress)
        );
        uploadMs = Date.now() - uploadStart;
        console.log(`[PHOTO-UPLOAD] Photo uploaded to S3`);
        const fileId = uuidv4();
        fileUuid = fileId;
        const timestamp = dayjs().valueOf();
        const status = options?.shouldSendToAI ? 'pending' : 'corrected';
        await this.DB.addFileData(photoId, photoName, photoTag, {
          uuid: fileId,
          data: fileKeys.publicUrl,
          ext: uploadExt,
          analysisResult: { status, messages: [] },
          timestamp
        });
        const location = await this.fileService.getDeviceLocation();
        const latitude = location ? location.latitude.toString() : '0';
        const longitude = location ? location.longitude.toString() : '0';
        // Ojo: latitud y longitud quedan en alcance desde acá. NO van al evento de telemetría —
        // el armado usa lista blanca justamente para que agregarlas requiera un cambio deliberado,
        // y `npm run verify:upload-telemetry` falla si alguna vez sobreviven.
        const declarePhotoProps = {
          photo_id: photoId,
          latitude: latitude,
          longitude: longitude,
          host_path: fileKeys.hostPath,
          key_path: fileKeys.keyPath,
          step_id: EInspectionStep.Photo,
          description: '',
        } as IBodyUploadFile;
        console.log(`[PHOTO-UPLOAD] Declaring photo to API...`);
        await this.inspectionApiService.declarePhoto(declarePhotoProps);
        console.log(`[PHOTO-UPLOAD] Photo declared to API`);
        const endTime = Date.now();
        console.log(`[PHOTO-UPLOAD] Finished: photoId=${photoId}, ext=${ext}, duration=${endTime - startTime}ms`);

        const aIAvailable = this.aiDataService.getHasAIAvailable();
        if (status === 'pending' && aIAvailable) {
          await this.analyzePhoto(photoId, photoTag, fileId, fileKeys.publicUrl);
          // La foto entra en `pending`: se arma el temporizador para que salga sola si el
          // veredicto no llega. Sin esto quedaria esperando hasta la proxima navegacion.
          void this.scheduleDegradationCheck();
        }
      } catch (error: any) {
        console.log(`[PHOTO-UPLOAD] Error:`, error);
        // Un rechazo del ARCHIVO no se encola, aunque ocurra durante la subida y no antes. La
        // condición que lo rechaza está en la firma de la URL prefirmada, así que el reintento
        // produce exactamente el mismo rechazo: encolarlo es una cola que no drena.
        //
        // Y se re-lanza TAL CUAL, sin envolverlo: perder el tipo haría que photo.page lo reporte a
        // Sentry en vez de mostrar el motivo, y que `uploadFailedPhotos` lo cuente como fallo
        // sistémico. Fijado por `npm run verify:retry-policy`, invariante 4.
        if (error instanceof PhotoRejectedError) {
          throw error;
        }
        await this.DB.addNotUploadedFileData({
          photoId,
          photoName,
          photoTag,
          blob: file,
          ext,
          // Se persiste la intención original: al reintentar desde la cola,
          // una foto que iba a la IA debe seguir yendo (si se pierde, entra
          // como 'corrected' y queda exenta del análisis para siempre).
          shouldSendToAI: options?.shouldSendToAI ?? false,
          // Y el método de captura por la misma razón: sin esto, una foto reintentada reporta
          // origen desconocido y contamina justo el dato que 426 necesita correlacionar.
          captureMethod: options?.captureMethod,
        })
        // La OI no va en el mensaje: el mensaje es el título del issue en Sentry, y un
        // identificador ahí abre un issue distinto por inspección. Viaja como tag desde el
        // captureException de photo.page.ts, que es donde sí agrupa y filtra.
        const message = `Error al subir la foto justo en la función de prefirmadas y declaración: ${photoId} - ${photoName}. ${error?.message ?? ''}`;
        throw new Error(message);
      }

      this.telemetry.emit({
        attemptId, photoId, ext, captureMethod: options?.captureMethod,
        origin: 'inspeccion',
        outcome: 'success',
        declaredSizeBytes: options?.declaredSizeBytes,
        realSizeBytes: options?.realSizeBytes,
        uploadedSizeBytes, darkPixelRatio, prepareMs, uploadMs,
        totalMs: Date.now() - startTime,
        fileUuid, keyPath,
      });
    } catch (error: any) {
      this.telemetry.emit({
        attemptId, photoId, ext, captureMethod: options?.captureMethod,
        origin: 'inspeccion',
        outcome: error instanceof PhotoRejectedError ? 'client_rejection' : 'upload_failure',
        rejectionCause: error instanceof PhotoRejectedError ? error.name : undefined,
        declaredSizeBytes: options?.declaredSizeBytes,
        realSizeBytes: options?.realSizeBytes,
        uploadedSizeBytes, darkPixelRatio, prepareMs, uploadMs,
        totalMs: Date.now() - startTime,
        fileUuid, keyPath,
      });
      throw error;
    }
  }

  public async addOptionalPhoto(
    photoId: number,
    photoName: string,
    photoTag: string,
    data: string,
  ) {
    const timestamp = dayjs().valueOf();
    const fileId = uuidv4();
    await this.DB.addFileData(photoId, photoName, photoTag,{
      data,
      uuid: fileId,
      ext: EExtension.jpg,
      timestamp,
    }, true);
  }

  private async analyzePhoto(
    photoId: number,
    photoTag: string,
    uuid: string,
    url: string
  ) {
    const photoData = await this.DB.getFileData(photoId);
    if (!photoData) {
      throw new Error(`No se encontró la foto con ID ${photoId}`);
    }
    // TODO: Acá chequear si ya hay alguna foto del step marcada como correcta.
    const analysisRequest: IPhotoAnalysisClientRequest = {
      photo_id: photoId,
      uuid,
      photo_tag: photoTag,
      url,
    };
    await this.inspectionApiService.analyzePhoto(analysisRequest);
  }

  public async deleteOptionalPhotoTmp(photoId: number) {
    const photoData = await this.DB.getFileData(photoId);
    if (photoData) {
      photoData.isSkipped = false;
      photoData.files = [];
      await this.DB.updateFileData(photoId, photoData);
    }
  }

  async isPhotoSkipped(photoId: number): Promise<boolean> {
    const photoData = await this.DB.getFileData(photoId);
    return photoData?.isSkipped ?? false;
  }

  /**
   * Reintenta la cola de fotos fallidas EN SERIE, nunca en paralelo: cada
   * subida fallida re-encola y reintenta, así que N subidas concurrentes
   * contra un backend degradado se amplifican en una tormenta de requests
   * (medido en producción: ~50 requests en 34 s con ~10 fotos en cola).
   * Si una foto falla se continúa con las demás; dos fallos CONSECUTIVOS
   * indican caída sistémica (gateway/red) y se corta para no dejar al
   * usuario esperando — lo no intentado permanece en cola.
   */
  async uploadFailedPhotos(): Promise<void> {
    // Fila por fila, sin agrupar por paso: bajo un mismo `photoId` puede haber capturas DISTINTAS
    // —con `max_photos > 1`, o con 1, porque la captura encolada no cuenta como tomada y el asegurado
    // puede sacar otra—, y cada una se intenta antes de salir de la cola. Si el paso queda con más
    // archivos que `max_photos`, es a propósito: un sobrante se revisa, una captura perdida no.
    const notUploadedPhotos = await this.DB.getAllNotUploadedPhotos();
    console.log(`[PHOTO-UPLOAD] Reintentando ${notUploadedPhotos.length} foto(s) en cola`);

    const failedNames: string[] = [];
    let lastError: unknown;
    let consecutiveFailures = 0;

    for (const photo of notUploadedPhotos) {
      try {
        await this.addPhotoAndDeclare(
          photo.photoId,
          photo.photoName,
          photo.photoTag,
          photo.blob,
          photo.ext,
          {
            shouldSendToAI: photo.shouldSendToAI ?? false,
            captureMethod: photo.captureMethod,
          },
        );
        consecutiveFailures = 0;
      } catch (error) {
        failedNames.push(photo.photoName);
        lastError = error;
        // Un rechazo del archivo —peso, 0 bytes, ilegible, negra— es un fallo PERMANENTE de esa
        // foto (no se re-encoló, y el `finally` la saca de la cola), no una señal de caída
        // sistémica: no debe activar el corte del loop.
        if (error instanceof PhotoRejectedError) {
          continue;
        }
        consecutiveFailures++;
        if (consecutiveFailures >= 2) {
          break;
        }
      } finally {
        // Se borra DESPUÉS del intento, y solo la fila intentada. Borrar antes abre una ventana en
        // la que la captura no está ni en la cola ni en S3: si la app muere ahí, la foto se pierde
        // sin rastro. La fila que el fallo acaba de re-encolar es otra, con otro `id`.
        await this.discardQueuedRow(photo);
      }
    }

    if (failedNames.length > 0) {
      const detail =
        lastError instanceof Error ? lastError.message : String(lastError ?? '');
      throw new Error(
        `No se pudieron subir ${failedNames.length} foto(s) pendiente(s): ${failedNames.join(', ')}. Último error: ${detail}`
      );
    }
  }

  /**
   * Saca de la cola la fila ya intentada, por su clave.
   *
   * No se usa el borrado por `photoId` porque bajo el mismo paso conviven otras filas —la que el
   * catch re-encoló y las demás capturas del paso— y el masivo se las llevaría, que es exactamente
   * la pérdida que este método existe para evitar.
   *
   * Una fila sin `id` no se puede borrar sin arrastrar a las otras, así que se deja: la cola queda
   * con basura y se reintenta, que es preferible a perder la captura. En la práctica no ocurre —el
   * store es `++id` y toda fila leída lo trae— y por eso se loguea en vez de silenciarse.
   */
  private async discardQueuedRow(row: INotUploadedFileData): Promise<void> {
    if (row.id === undefined) {
      console.warn('[PHOTO-UPLOAD] Fila de la cola sin id; se deja para el próximo intento', {
        photoId: row.photoId,
      });
      return;
    }
    try {
      await this.DB.deleteNotUploadedFileById(row.id);
    } catch (error) {
      console.warn('[PHOTO-UPLOAD] No se pudo sacar la fila de la cola', { id: row.id, error });
    }
  }

  /**
   * Trabajo que corre al retomar una inspección vigente, antes de que el asegurado siga.
   *
   * El orden es el criterio: **subir antes de cualquier limpieza de estado local**, y consultar los
   * veredictos persistidos **antes** del degradado por tiempo. Al revés, una foto cuyo veredicto
   * existe pero se perdió con la app cerrada pasaría primero por «Nuestra IA no pudo analizar tu
   * foto».
   *
   * **No borra nada.** El endpoint devuelve veredictos, no declaraciones: que una foto no esté en la
   * lista no dice que falte. Conservar basura es recuperable, perder una captura del asegurado no.
   *
   * **Nunca lanza.** Corre desde el resolver de la ruta: una excepción acá deja al asegurado fuera
   * de su propia inspección por un fallo de red que no le impide seguir sacando fotos.
   *
   * El plazo de la consulta lo elige el llamador (`PHOTO_STATE_TIMEOUT_ON_RESUME_MS` al entrar,
   * `..._ON_CLOSE_MS` al cerrar), y por eso es obligatorio: un default lo heredaría el camino que
   * no lo pensó.
   */
  public async resumePendingWork(stateTimeoutMs: number): Promise<void> {
    try {
      await this.uploadFailedPhotos();
    } catch (error) {
      console.warn('[RESUME] Quedaron fotos sin subir al retomar; siguen en cola', error);
    }
    await this.reconcileWithBackend(stateTimeoutMs);
    // Al retomar es cuando más importa: si el veredicto se perdió con la app cerrada, el plazo ya
    // venció y la foto tiene que salir de `pending` ahora, no dentro de dos minutos más.
    await this.degradeStalePendingPhotos();
  }

  /**
   * Aplica los veredictos persistidos que el teléfono no recibió. Un 401 o un fallo del backend no
   * cambian nada local: el contrato dice que ninguno de los dos invalida lo que hay en el teléfono.
   * **Nunca lanza.**
   */
  public async reconcileWithBackend(timeoutMs: number): Promise<void> {
    try {
      const outcome = await this.inspectionApiService.getPhotoAnalysisState(timeoutMs);
      if (outcome.kind !== 'ok') {
        console.warn('[RESUME] No se pudo consultar el estado de las fotos; se conserva el local', outcome);
        return;
      }
      if (outcome.dropped > 0) {
        console.warn(`[RESUME] ${outcome.dropped} estado(s) de foto con forma inválida, ignorados`);
      }
      const plan = planReconciliation(await this.DB.getAllFiles(), outcome.items);
      for (const verdict of plan) {
        // La condición se vuelve a mirar sobre la entrada releída: si Pusher escribió entre el plan y
        // acá, su veredicto trae motivos que el endpoint no tiene y no se pisa con el respaldo.
        await this.writeAnalysisResult(
          verdict.photoId,
          verdict.fileIdx,
          { status: verdict.status, messages: verdict.messages },
          (current) => current === 'corrected' || current === verdict.status,
        );
      }
    } catch (error) {
      console.warn('[RESUME] Falló la reconciliación con el backend; se conserva el estado local', error);
    }
  }

  /**
   * Saca de `pending` las fotos cuyo veredicto nunca llegó, y las deja en `analysis_failed` **con su
   * mensaje**.
   *
   * El mensaje no es decorativo: `photo-item` condiciona el bloque de texto a que
   * `getProcessedMessages()` devuelva algo, así que un `analysis_failed` con `messages` vacío daría
   * una foto borrable pero igual de muda. Con el mensaje puesto, el estado funciona entero y no hay
   * que tocar ni la plantilla ni `canDelete`, que ya contempla `analysis_failed`.
   *
   * **Nunca lanza:** corre en el camino de retomar y desde un temporizador; un fallo acá no puede
   * voltear la navegación ni la inspección.
   */
  public async degradeStalePendingPhotos(nowMs: number = Date.now()): Promise<number> {
    try {
      const entries = await this.DB.getAllFiles();
      const stale = findStalePending(entries, nowMs);
      if (stale.length === 0) {
        return 0;
      }
      const touched = new Map<number, PhotoStep>();
      for (const { photoId, fileIdx, waitedMs } of stale) {
        const entry = touched.get(photoId) ?? entries.find((e) => e.photoId === photoId);
        if (!entry) {
          continue;
        }
        const files = [...entry.files];
        files[fileIdx] = {
          ...files[fileIdx],
          analysisResult: {
            status: 'analysis_failed',
            messages: [ANALYSIS_FAILED_MESSAGE],
          },
        };
        touched.set(photoId, { ...entry, files });
        console.warn(
          `[ANALISIS] Sin veredicto tras ${Math.round(waitedMs / 1000)} s; se degrada a analysis_failed`,
          { photoId, fileIdx }
        );
      }
      for (const entry of touched.values()) {
        await this.DB.Photo.put(entry);
        this.photoResultsAdded$.emit({ photoId: entry.photoId });
      }
      return stale.length;
    } catch (error) {
      console.warn('[ANALISIS] No se pudo degradar las fotos sin veredicto', error);
      return 0;
    }
  }

  /**
   * Programa **un** chequeo al vencimiento exacto del `pending` más viejo, en vez de sondear.
   *
   * Se reprograma sola mientras queden fotos esperando y se apaga cuando no quedan. Idempotente: una
   * segunda llamada reemplaza el temporizador anterior en vez de sumar otro.
   */
  public async scheduleDegradationCheck(): Promise<void> {
    if (this.degradationTimer) {
      clearTimeout(this.degradationTimer);
      this.degradationTimer = null;
    }
    let waitMs: number | null;
    try {
      waitMs = msUntilNextDegradation(await this.DB.getAllFiles(), Date.now());
    } catch {
      return;
    }
    if (waitMs === null) {
      return;
    }
    this.degradationTimer = setTimeout(() => {
      this.degradationTimer = null;
      void this.degradeStalePendingPhotos().then(() => this.scheduleDegradationCheck());
    }, waitMs);
  }

  /**
   * Fotos obligatorias que faltan. Es la única condición que puede impedir cerrar la inspección:
   * la calidad de imagen es advertencia y no bloquea, por definición del autor de la historia.
   *
   * Se leen las dos tablas porque una foto capturada que no llegó a subirse **no está** en `Photo`
   * —`addFileData` corre después del PUT— y solo vive en la cola. Sin ese segundo dato el mensaje
   * le pediría al asegurado volver a fotografiar algo que ya fotografió.
   */
  public async getMissingRequiredPhotos(
    required: RequiredPhotoRef[]
  ): Promise<MissingRequiredPhoto[]> {
    const stored = await this.DB.getAllFiles();
    const queued = await this.DB.getAllNotUploadedPhotos();
    return findMissingRequiredPhotos(required, stored, queued);
  }

  /**
   * La decisión del cierre: la del teléfono más la confirmación de S3 de que cada archivo que dio por
   * subido existe. Sin respuesta de S3 decide como `getMissingRequiredPhotos`.
   *
   * Un archivo que S3 confirma que falta se descarta del teléfono antes de devolver. Sin eso el asegurado
   * podría quedar sin salida: el paso deshabilita la cámara con el máximo de archivos, y la galería no deja
   * borrar una foto en `success` o `pending`. Descartado, el paso lo deja repetirla.
   */
  public async verifyRequiredPhotosForClose(
    required: RequiredPhotoRef[],
    timeoutMs: number,
  ): Promise<{ missing: MissingRequiredPhoto[]; storage: StorageCheckOutcome | null }> {
    const stored = await this.DB.getAllFiles();
    const queued = await this.DB.getAllNotUploadedPhotos();
    const names = requiredStorageFileNames(required, stored);
    const storage = names.length > 0 ? await this.inspectionApiService.checkStorageFiles(names, timeoutMs) : null;
    const missing = findMissingRequiredPhotos(required, stored, queued, storage?.kind === 'ok' ? storage.exists : undefined);
    for (const photo of missing) {
      if (photo.missingFileNames?.length) {
        await this.discardLocalFiles(photo.photoId, photo.missingFileNames);
      }
    }
    return { missing, storage };
  }

  /**
   * Saca solo los archivos con esos nombres en S3, de todas las filas de la foto: el índice de `Photo` no
   * es único por `photoId`. Cada fila se reescribe con su `id`, y el aviso refresca el resumen.
   */
  private async discardLocalFiles(photoId: number, names: string[]): Promise<void> {
    const discard = new Set(names);
    const rows = (await this.DB.getAllFiles()).filter((row) => row.photoId === photoId);
    for (const row of rows) {
      const files = row.files.filter((file) => {
        const name = storageFileNameOf(file.data);
        return name === null || !discard.has(name);
      });
      if (files.length !== row.files.length) {
        await this.DB.updateFileData(photoId, { ...row, files });
      }
    }
    this.photoResultsAdded$.emit({ photoId });
  }

  public async handlePhotoAnalysisResults(
    input: IPhotoAnalysisNotification
  ) {
    const { result } = input;
    // Se cargan todas las entradas y no solo `getFileData(photo_id)` porque el evento puede venir sin
    // `photo_id`: la Lambda identifica por key. Son unas pocas decenas de entradas por inspección, y
    // un solo camino de resolución es preferible a dos que puedan divergir.
    const entries = await this.DB.getAllFiles();
    const match = locateAnalyzedFile(entries, input);
    if (!match.found) {
      // Se distingue el motivo a propósito: `ambiguous_key` NO es «no la encontré», es «encontré más
      // de una y marcar la equivocada es peor que no marcar ninguna».
      console.warn(
        `No se pudo localizar la foto del análisis (${match.reason})`,
        { key: input.key, photo_id: input.photo_id, uuid: input.uuid }
      );
      return;
    }
    await this.writeAnalysisResult(match.photoId, match.fileIdx, result);
  }

  /**
   * Escribe un veredicto del análisis sobre un archivo ya localizado. Lo usan la notificación en vivo
   * y la reconciliación al retomar, así que las dos aplican la misma regla de `corrected`.
   *
   * Relee la entrada en vez de recibirla: dos veredictos seguidos sobre el mismo paso, escritos desde
   * una copia leída antes, harían que el segundo pisara al primero.
   */
  private async writeAnalysisResult(
    photoId: number,
    fileIdx: number,
    result: { status: TanalysisStatus; messages: string[] },
    skipIfCurrent?: (currentStatus: string | undefined) => boolean,
  ): Promise<void> {
    const photoEntry = await this.DB.getFileData(photoId);
    const targetFile = photoEntry?.files[fileIdx];
    if (!photoEntry || !targetFile) {
      console.warn('[ANALISIS] La foto del veredicto ya no está en el teléfono', { photoId, fileIdx });
      return;
    }
    if (skipIfCurrent?.(targetFile.analysisResult?.status)) {
      return;
    }
    // La asimetría con el degradado por tiempo es deliberada, y este es el principio:
    // **un estado que codifica una ACCIÓN del asegurado es firme; uno que codifica una SUPOSICIÓN
    // nuestra, no.** `corrected` significa «sacó otra foto», así que un veredicto que llega tarde no
    // puede volver a marcarle algo que ya arregló. En cambio `analysis_failed` puesto por plazo
    // vencido significa «asumimos que el veredicto no va a llegar», y si llega, la suposición cede
    // ante el dato — por eso ese sí se deja pisar.
    // Sin este comentario la próxima lectura ve una inconsistencia y la «arregla» por consistencia.
    const status = (targetFile.analysisResult?.status === 'corrected' ? 'corrected' : result.status) as TanalysisStatus;
    const updatedFile = {
      ...targetFile,
      analysisResult: {
        status,
        messages: result.messages,
      }
    };
    const updatedFiles = [...photoEntry.files];
    updatedFiles[fileIdx] = updatedFile;
    await this.DB.Photo.put({
      ...photoEntry,
      files: updatedFiles
    });
    this.photoResultsAdded$.emit({ photoId });
  }

  async checkPhotoStepStatus(photoId: number): Promise<{
    shouldSendToAI: boolean;
    shouldAlertUser: boolean
  }> {
    const hasReachedFinalStep = localStorage.getItem(HAS_REACHED_FINAL_STEP);
    const entry = await this.DB.getFileData(photoId);
    if (!entry) {
      return { shouldSendToAI: true, shouldAlertUser: false };
    }
    const files = entry.files ?? [];
    const realFiles = files.filter(f => !!f.data && !!f.ext);
    if (realFiles.length === 0) {
      return { shouldSendToAI: true, shouldAlertUser: false };
    }
    const hasWrongPhotos = realFiles.some(f => f.analysisResult?.status === 'error');
    const hasSuccess = realFiles.some(f => f.analysisResult?.status === 'success');
    const hasCorrected = realFiles.some(f => f.analysisResult?.status === 'corrected');
    const shouldAlertUser = !hasSuccess && !hasCorrected && hasWrongPhotos;
    const shouldSendToAI = !hasSuccess && !hasCorrected && !hasReachedFinalStep;
    return { shouldSendToAI, shouldAlertUser };
  }

  public async getPhotoSectionWithWrongAnalysis(
    photoIdx?: number,
    direction: 'next' | 'prev' = 'next'
  ): Promise<number | null> {
    const allPhotoSections = await this.DB.getAllFiles();

    let photoSections = allPhotoSections;

    if (photoIdx !== undefined) {
      if (photoIdx < 0 || photoIdx >= allPhotoSections.length) {
        console.warn(`photoIdx ${photoIdx} está fuera de rango (0-${allPhotoSections.length - 1})`);
        return null;
      }

      photoSections = direction === 'next'
        ? allPhotoSections.slice(photoIdx + 1)
        : allPhotoSections.slice(0, photoIdx);

      if (photoSections.length === 0) {
        const directionText = direction === 'next' ? 'después' : 'antes';
        console.log(`No hay más fotos ${directionText} del índice actual`);
        return null;
      }
    }

    console.log(`Photo sections to check for wrong analysis (${direction}):`, photoSections);

    const sectionsToCheck = direction === 'prev'
      ? [...photoSections].reverse()
      : photoSections;

    for (const section of sectionsToCheck) {
      if (this.sectionHasWrongAnalysis(section)) {
        return section.photoId;
      }
    }

    return null;
  }

  private sectionHasWrongAnalysis(section: PhotoStep): boolean {
    const hasWrong = section.files.some(file => file.analysisResult?.status === 'error');
    const hasSuccess = section.files.some(file => file.analysisResult?.status === 'success');
    return hasWrong && !hasSuccess;
  }
}
